/**
 * Service price list (OCP-851) - the "Preisliste für Servicearbeiten" AGB V4
 * (OWS-D23) 6.6 points at, on the admin legal page (/terms).
 *
 * A versioned reference, NOT an accepted document: no acceptance, no 13.3
 * notice, no rejection; publishing needs no new AGB version (operator
 * decision 2026-10-13). Drafts are created and edited here; publishing takes
 * an effective date and an audited reason and freezes the version. From its
 * effective date the order summary names the version and every order row
 * stamps it. With no list published, 6.6 is not applied and ordering is not
 * blocked. A published version whose effective date still lies ahead
 * (scheduled) can be withdrawn here with an audited reason; it is then never
 * served and shows as withdrawn. Same REST routes as the MCP tools
 * `ocp_*_service_price_*`.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError, readErrorKey } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

export type ServicePriceUnit = 'per_started_15_min' | 'per_hour' | 'flat';

export const SERVICE_PRICE_UNIT_LABEL: Record<ServicePriceUnit, string> = {
  per_started_15_min: 'per started 15 min',
  per_hour: 'per hour',
  flat: 'flat',
};

export interface ServicePriceItem {
  name_de: string;
  name_en: string;
  unit: ServicePriceUnit;
  net_amount_cents: number;
  note_de: string | null;
  note_en: string | null;
}

export interface ServicePriceVersion {
  id: string;
  version: string;
  status: 'draft' | 'published' | 'withdrawn';
  items: ServicePriceItem[];
  note_de: string | null;
  note_en: string | null;
  effective_from: string | null;
  published_at: string | null;
  published_by: string | null;
  change_note: string | null;
  content_sha256: string | null;
  withdrawn_at: string | null;
  withdrawn_by: string | null;
  withdraw_reason: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  current: boolean;
  scheduled: boolean;
}

/** Editor row: the amount is typed as a EUR string ("95", "95,50"). */
interface ItemRow {
  name_de: string;
  name_en: string;
  unit: ServicePriceUnit;
  amount: string;
  note_de: string;
  note_en: string;
}

const EMPTY_ROW: ItemRow = { name_de: '', name_en: '', unit: 'per_hour', amount: '', note_de: '', note_en: '' };

/** "95" / "95,5" / "95.50" -> 9550; null when not a valid non-negative amount. */
export function parseEurToCents(input: string): number | null {
  const v = input.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  return Math.round(Number(v) * 100);
}

export function formatCents(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} EUR`;
}

function toRow(i: ServicePriceItem): ItemRow {
  return { name_de: i.name_de, name_en: i.name_en, unit: i.unit, amount: (i.net_amount_cents / 100).toFixed(2).replace('.', ','), note_de: i.note_de ?? '', note_en: i.note_en ?? '' };
}

const ERROR_TEXT: Record<string, string> = {
  version_exists: 'That version label already exists.',
  not_draft: 'This version is published and immutable - create a new version instead.',
  items_required: 'A price list needs at least one item.',
  effective_before_now: 'The effective date must not lie in the past.',
  reason_required: 'Give a reason of at least 10 characters.',
  version_not_found: 'The version no longer exists.',
  not_withdrawable: 'Only a scheduled version (effective date still ahead) can be withdrawn. A version in force is replaced by publishing a newer one.',
};

function errorText(err: unknown, fallback: string): string {
  const key = err instanceof ApiError ? readErrorKey(err.body) : null;
  if (key && ERROR_TEXT[key]) return ERROR_TEXT[key]!;
  return err instanceof ApiError ? `${fallback} (HTTP ${err.status}).` : `${fallback}.`;
}

function statusBadge(v: ServicePriceVersion) {
  const [label, cls] =
    v.status === 'draft' ? ['draft', 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300']
    : v.current ? ['in force', 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300']
    : v.status === 'withdrawn' ? ['withdrawn', 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300']
    : v.scheduled ? ['scheduled', 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300']
    : ['superseded', 'bg-gray-50 dark:bg-gray-800 text-gray-500'];
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${cls}`}>{label}</span>;
}

export function ServicePriceListSection() {
  const [versions, setVersions] = useState<ServicePriceVersion[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** null = editor closed, 'new' = new draft, else the draft id being edited. */
  const [editing, setEditing] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  /** Version id whose withdraw form is open, its reason, and the outcome. */
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  async function onWithdraw(id: string) {
    setWithdrawBusy(true);
    setWithdrawError(null);
    try {
      await api(`/v1/admin/service-prices/versions/${id}/withdraw`, { method: 'POST', body: { reason: withdrawReason.trim() } });
      setWithdrawing(null);
      setWithdrawReason('');
      await load();
    } catch (err) {
      setWithdrawError(errorText(err, 'Withdraw failed'));
      await load();
    } finally {
      setWithdrawBusy(false);
    }
  }

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api<{ items: ServicePriceVersion[] }>('/v1/admin/service-prices/versions');
      setVersions(r.items);
    } catch (err) {
      setLoadError(err instanceof ApiError ? `Could not load the service price list (HTTP ${err.status}).` : 'Could not load the service price list.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const draft = editing && editing !== 'new' ? versions?.find((v) => v.id === editing) ?? null : null;
  const inForce = versions?.find((v) => v.current) ?? null;

  return (
    <section className="mt-10" data-testid="service-prices">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Service price list (AGB 6.6)</h2>
        <button
          type="button"
          onClick={() => setEditing(editing === 'new' ? null : 'new')}
          data-testid="service-prices-new"
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          {editing === 'new' ? 'Close' : 'New draft'}
        </button>
      </div>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        The &quot;Preisliste für Servicearbeiten&quot; AGB 6.6 refers to (handling an unjustified defect report caused by
        gross negligence). A versioned reference, not an accepted document: publishing sends no notice and needs no new
        AGB version. The version in force is named on every order summary and stamped on the order; the public text is
        at /service-prices on the portal. Net EUR prices (B2B).
      </p>
      {loadError && (
        <p role="alert" className="mt-2 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {loadError}
        </p>
      )}
      {versions && !inForce && (
        <p className="mt-2 text-sm text-amber-700 dark:text-amber-300" data-testid="service-prices-none">
          No price list in force. AGB 6.6 is not applied: the order summary omits the line, ordering is not blocked.
        </p>
      )}

      {versions && versions.length > 0 && (
        <table className="mt-2 w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="py-1 pr-3">Version</th>
              <th className="py-1 pr-3">Status</th>
              <th className="py-1 pr-3">Effective</th>
              <th className="py-1 pr-3">Items</th>
              <th className="py-1 pr-3">Published</th>
              <th className="py-1 pr-3">Reason</th>
              <th className="py-1 pr-3" />
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id} className="border-t border-gray-100 dark:border-gray-800 align-top" data-testid={`service-price-version-${v.version}`}>
                <td className="py-1.5 pr-3 font-mono">{v.version}</td>
                <td className="py-1.5 pr-3">{statusBadge(v)}</td>
                <td className="py-1.5 pr-3 tabular-nums">{v.effective_from ? fmtDateTime(v.effective_from) : '-'}</td>
                <td className="py-1.5 pr-3">
                  <button type="button" onClick={() => setExpanded(expanded === v.id ? null : v.id)} className="text-brand-600 hover:underline">
                    {v.items.length}
                  </button>
                  {expanded === v.id && (
                    <ul className="mt-1 text-xs text-gray-600 dark:text-gray-400" data-testid={`service-price-items-${v.version}`}>
                      {v.items.map((i, idx) => (
                        <li key={idx}>
                          {i.name_de} / {i.name_en}: {formatCents(i.net_amount_cents)} {SERVICE_PRICE_UNIT_LABEL[i.unit]}
                          {i.note_de ? ` (${i.note_de})` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-xs text-gray-600 dark:text-gray-400">
                  {v.published_at ? `${fmtDateTime(v.published_at)} by ${v.published_by}` : `draft by ${v.created_by}`}
                  {v.content_sha256 && <span className="block font-mono text-[10px] text-gray-400">sha256 {v.content_sha256.slice(0, 12)}</span>}
                </td>
                <td className="py-1.5 pr-3 text-xs text-gray-600 dark:text-gray-400">
                  {v.change_note ?? '-'}
                  {v.status === 'withdrawn' && (
                    <span className="mt-1 block text-red-700 dark:text-red-300" data-testid={`service-price-withdrawn-${v.version}`}>
                      Withdrawn {v.withdrawn_at ? fmtDateTime(v.withdrawn_at) : ''} by {v.withdrawn_by}: {v.withdraw_reason}
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-right">
                  {v.status === 'draft' && (
                    <button
                      type="button"
                      onClick={() => setEditing(editing === v.id ? null : v.id)}
                      data-testid={`service-price-edit-${v.version}`}
                      className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      {editing === v.id ? 'Close' : 'Edit'}
                    </button>
                  )}
                  {v.status === 'published' && v.scheduled && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setWithdrawing(withdrawing === v.id ? null : v.id);
                          setWithdrawReason('');
                          setWithdrawError(null);
                        }}
                        data-testid={`service-price-withdraw-${v.version}`}
                        className="rounded border border-red-300 dark:border-red-700 px-2 py-1 text-xs font-semibold text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                      >
                        {withdrawing === v.id ? 'Close' : 'Withdraw'}
                      </button>
                      {withdrawing === v.id && (
                        <div className="mt-2 text-left" data-testid={`service-price-withdraw-form-${v.version}`}>
                          <label className="block">
                            <span className="block text-xs text-gray-500">Reason for withdrawing (min 10 chars, audited)</span>
                            <input
                              value={withdrawReason}
                              onChange={(e) => setWithdrawReason(e.target.value)}
                              aria-label={`Reason for withdrawing ${v.version}`}
                              className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
                            />
                          </label>
                          {withdrawError && (
                            <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{withdrawError}</p>
                          )}
                          <button
                            type="button"
                            disabled={withdrawBusy || withdrawReason.trim().length < 10}
                            onClick={() => void onWithdraw(v.id)}
                            data-testid={`service-price-withdraw-confirm-${v.version}`}
                            className="mt-1 rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
                          >
                            {withdrawBusy ? 'Working…' : 'Withdraw version'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <DraftEditor
          key={editing}
          draft={draft}
          onChanged={async (closeEditor) => {
            await load();
            if (closeEditor) setEditing(null);
          }}
        />
      )}
    </section>
  );
}

function DraftEditor({
  draft,
  onChanged,
}: {
  draft: ServicePriceVersion | null;
  onChanged: (closeEditor: boolean) => Promise<void>;
}) {
  // the draft this editor writes to: the one opened, or the one its first save created
  const [draftId, setDraftId] = useState<string | null>(draft?.id ?? null);
  const [version, setVersion] = useState(draft?.version ?? '');
  const [noteDe, setNoteDe] = useState(draft?.note_de ?? '');
  const [noteEn, setNoteEn] = useState(draft?.note_en ?? '');
  const [rows, setRows] = useState<ItemRow[]>(draft && draft.items.length > 0 ? draft.items.map(toRow) : [{ ...EMPTY_ROW }]);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  function setRow(idx: number, patch: Partial<ItemRow>) {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  /** Validated payload, or an error message naming the first bad row. */
  function payload(): { body: Record<string, unknown> } | { error: string } {
    const items: ServicePriceItem[] = [];
    for (const [idx, r] of rows.entries()) {
      const blank = !r.name_de.trim() && !r.name_en.trim() && !r.amount.trim();
      if (blank) continue;
      const cents = parseEurToCents(r.amount);
      if (!r.name_de.trim() || !r.name_en.trim()) return { error: `Item ${idx + 1}: German and English name are required.` };
      if (cents === null) return { error: `Item ${idx + 1}: enter the net amount in EUR, e.g. 95 or 95,50.` };
      items.push({ name_de: r.name_de.trim(), name_en: r.name_en.trim(), unit: r.unit, net_amount_cents: cents, note_de: r.note_de.trim() || null, note_en: r.note_en.trim() || null });
    }
    return { body: { version: version.trim(), items, note_de: noteDe.trim() || null, note_en: noteEn.trim() || null } };
  }

  async function save(): Promise<string | null> {
    const p = payload();
    if ('error' in p) {
      setError(p.error);
      return null;
    }
    if (draftId) {
      await api(`/v1/admin/service-prices/drafts/${draftId}`, { method: 'PATCH', body: p.body });
      return draftId;
    }
    const r = await api<{ version: ServicePriceVersion }>('/v1/admin/service-prices/drafts', { method: 'POST', body: p.body });
    setDraftId(r.version.id);
    return r.version.id;
  }

  async function onSave() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const id = await save();
      if (id) {
        setResult('Draft saved.');
        await onChanged(false);
      }
    } catch (err) {
      setError(errorText(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  }

  async function onPublish() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const id = await save();
      if (!id) return;
      await api(`/v1/admin/service-prices/versions/${id}/publish`, {
        method: 'POST',
        body: { reason: reason.trim(), ...(effectiveFrom ? { effective_from: new Date(effectiveFrom).toISOString() } : {}) },
      });
      await onChanged(true);
    } catch (err) {
      setError(errorText(err, 'Publish failed'));
      // a new draft may have been created before the publish step failed
      await onChanged(false);
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!draftId) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/service-prices/drafts/${draftId}`, { method: 'DELETE' });
      await onChanged(true);
    } catch (err) {
      setError(errorText(err, 'Delete failed'));
    } finally {
      setBusy(false);
    }
  }

  const input = 'mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm';

  return (
    <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm" data-testid="service-prices-editor">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{draftId ? `Edit draft ${draft?.version ?? version}` : 'New draft'}</h3>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="block text-xs text-gray-500">Version label</span>
          <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="2026-10" data-testid="service-prices-version" className={`${input} font-mono`} />
        </label>
        <div />
        <label className="text-sm">
          <span className="block text-xs text-gray-500">General note (German, optional)</span>
          <textarea value={noteDe} onChange={(e) => setNoteDe(e.target.value)} rows={2} className={input} />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500">General note (English, optional)</span>
          <textarea value={noteEn} onChange={(e) => setNoteEn(e.target.value)} rows={2} className={input} />
        </label>
      </div>

      <h4 className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Items (net EUR, B2B)</h4>
      {rows.map((r, idx) => (
        <div key={idx} className="mt-2 grid grid-cols-1 gap-2 rounded border border-gray-100 dark:border-gray-800 p-2 sm:grid-cols-6" data-testid={`service-prices-row-${idx}`}>
          <input aria-label={`Item ${idx + 1} name (German)`} value={r.name_de} onChange={(e) => setRow(idx, { name_de: e.target.value })} placeholder="Leistung (de)" className={`${input} sm:col-span-2`} />
          <input aria-label={`Item ${idx + 1} name (English)`} value={r.name_en} onChange={(e) => setRow(idx, { name_en: e.target.value })} placeholder="Service (en)" className={`${input} sm:col-span-2`} />
          <select aria-label={`Item ${idx + 1} unit`} value={r.unit} onChange={(e) => setRow(idx, { unit: e.target.value as ServicePriceUnit })} className={input}>
            {(Object.keys(SERVICE_PRICE_UNIT_LABEL) as ServicePriceUnit[]).map((u) => (
              <option key={u} value={u}>{SERVICE_PRICE_UNIT_LABEL[u]}</option>
            ))}
          </select>
          <input aria-label={`Item ${idx + 1} net amount EUR`} value={r.amount} onChange={(e) => setRow(idx, { amount: e.target.value })} placeholder="95,00" inputMode="decimal" className={`${input} tabular-nums`} />
          <input aria-label={`Item ${idx + 1} note (German)`} value={r.note_de} onChange={(e) => setRow(idx, { note_de: e.target.value })} placeholder="Hinweis (de, optional)" className={`${input} sm:col-span-3`} />
          <input aria-label={`Item ${idx + 1} note (English)`} value={r.note_en} onChange={(e) => setRow(idx, { note_en: e.target.value })} placeholder="Note (en, optional)" className={`${input} sm:col-span-2`} />
          <button type="button" onClick={() => setRows((rs) => rs.filter((_, i) => i !== idx))} className="mt-1 rounded px-2 py-1 text-xs text-red-700 dark:text-red-300 hover:underline">
            Remove
          </button>
        </div>
      ))}
      <button type="button" onClick={() => setRows((rs) => [...rs, { ...EMPTY_ROW }])} data-testid="service-prices-add-row" className="mt-2 text-xs font-semibold text-brand-600 hover:underline">
        Add item
      </button>

      <div className="mt-4 grid grid-cols-1 gap-3 border-t border-gray-100 dark:border-gray-800 pt-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="block text-xs text-gray-500">Effective from (blank = now; never in the past)</span>
          <input type="datetime-local" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} data-testid="service-prices-effective" className={input} />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500">Reason for publishing (min 10 chars, audited)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="service-prices-reason" className={input} />
        </label>
      </div>

      {error && (
        <div role="alert" className="mt-3 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {result && <p className="mt-3 text-sm text-green-700 dark:text-green-300" data-testid="service-prices-result">{result}</p>}

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {draftId && (
          <button type="button" onClick={() => void onDelete()} disabled={busy} data-testid="service-prices-delete" className="rounded border border-red-300 dark:border-red-700 px-3 py-1.5 text-sm text-red-700 dark:text-red-300 disabled:opacity-50">
            Delete draft
          </button>
        )}
        <button type="button" onClick={() => void onSave()} disabled={busy || !version.trim()} data-testid="service-prices-save" className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 disabled:opacity-50">
          Save draft
        </button>
        <button
          type="button"
          onClick={() => void onPublish()}
          disabled={busy || !version.trim() || reason.trim().length < 10}
          data-testid="service-prices-publish"
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {busy ? 'Working…' : 'Publish'}
        </button>
      </div>
    </div>
  );
}
